/**
 * @author      sammy.dowds@zumper.com (Sammy Dowds)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { Dots } from '@zcode-ui/loaders'
import { toTitleCase } from '@zcode/nr/es/shared/utils'
import type { CardBackResponse, Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import { capitalize } from '@zcode/utils'
import cn from 'classnames'
import { format } from 'date-fns'
import React, { Fragment, type MouseEvent } from 'react'
import { ListingCardAmenities } from './ListingCardAmenities'
import { ListingCardDescription } from './ListingCardDescription'
import css from './ListingCardInfoSection.module.scss'
import { ListingCardReview } from './ListingCardReview'
import { ListingCardReviewsSummary } from './ListingCardReviewsSummary'
import { ListingCardSupplementalDescription } from './ListingCardSupplementalDescription'
import { getListCardAmenities } from './helpers/getListCardAmenities'

export interface ListingCardInfoSectionProps {
  cardBackData?: CardBackResponse
  cardBackDataHasLoaded?: boolean
  entity: Listable | Pin<RawPinInventorySearch>
  forceMobile?: boolean
  handleQuickLookClick: (e: MouseEvent<HTMLButtonElement>) => void
  isCity?: boolean
  isHood?: boolean
  isVertical?: boolean
  shouldShowDefaultInfo?: boolean
  isShortTerm: boolean
  updatedAmenities?: ReadonlyArray<string>
}

export const ListingCardInfoSection = ({
  cardBackData,
  cardBackDataHasLoaded,
  entity,
  forceMobile,
  handleQuickLookClick,
  isCity,
  isHood,
  isShortTerm,
  isVertical,
  shouldShowDefaultInfo,
  updatedAmenities,
}: ListingCardInfoSectionProps) => {
  const {
    dateAvailableDate,
    city,
    hoodCityText,
    propertyTypeText,
    review: entityReview,
    reviewsSummary: entityReviewsSummary,
    shortDescription,
    supplementalDescription: entitySupplementalDescription,
  } = entity

  const {
    review: localReview,
    reviews_summary: localReviewsSummary,
    supple_description: localSupplementalDescription,
  } = cardBackData?.[entity.groupId] ?? {}
  const review = entityReview ?? localReview
  const reviewsSummary = entityReviewsSummary ?? localReviewsSummary
  const supplementalDescription = entitySupplementalDescription ?? localSupplementalDescription

  let availableDateText = ''

  if (dateAvailableDate) {
    availableDateText = `Available ${format(dateAvailableDate, 'MMM d')}`
  }

  const frontCardAmenities = updatedAmenities ?? entity.combinedAmenities
  const slicedAmenities = frontCardAmenities.slice(0, 3)
  const titleCasedSlicedAmenities = slicedAmenities.map((amenity) => toTitleCase(amenity))
  const amenities = entity.combinedAmenities
  const { amenitiesList, otherSpacesBlurb } = getListCardAmenities(amenities)
  const dedupedAmenitiesList = amenitiesList.filter((amenity) => {
    return !titleCasedSlicedAmenities.includes(amenity)
  })
  const shouldFetchCardBack = shouldShowDefaultInfo || (isCity && !isVertical)

  if (!cardBackDataHasLoaded && shouldFetchCardBack) {
    return (
      <div className={css.loading}>
        <Dots />
      </div>
    )
  }

  return (
    <Fragment>
      <div className={cn(css.infoContainer, forceMobile && css.infoContainerMobile)}>
        <div className={css.typeAndBlurb}>
          {(hoodCityText || city) && (
            <p className={cn(css.typeAndBlurbText, forceMobile && css.typeAndBlurbTextMobile)}>
              {capitalize(propertyTypeText)} for rent
            </p>
          )}
          <p
            className={cn(
              css.typeAndBlurbText,
              css.blurbText,
              forceMobile && css.typeAndBlurbTextMobile
            )}
          >
            {isShortTerm ? otherSpacesBlurb : availableDateText}
          </p>
        </div>
        <button
          className={cn(css.closeButton, forceMobile && css.closeButtonMobile)}
          onClick={handleQuickLookClick}
          type="button"
        >
          Close
        </button>
      </div>
      <div
        className={`overflow-y-auto ${availableDateText ? 'h-[110px]' : 'h-[130px]'} flex flex-col gap-y-4 pb-4 no-scrollbar`}
      >
        {shouldShowDefaultInfo ? (
          <>
            {supplementalDescription && !isShortTerm ? (
              <ListingCardSupplementalDescription
                supplementalDescription={supplementalDescription}
              />
            ) : null}
            {!supplementalDescription && shortDescription && !isShortTerm ? (
              <ListingCardDescription description={shortDescription} />
            ) : null}
            {(!supplementalDescription && !shortDescription) || isShortTerm ? (
              <ListingCardAmenities amenitiesList={dedupedAmenitiesList} />
            ) : null}
            {reviewsSummary && !isShortTerm ? (
              <ListingCardReviewsSummary reviewsSummary={reviewsSummary} />
            ) : null}
            {!reviewsSummary && review ? <ListingCardReview review={review} /> : null}
          </>
        ) : (
          <>
            {isCity && !isVertical && supplementalDescription && !isShortTerm ? (
              <ListingCardSupplementalDescription
                supplementalDescription={supplementalDescription}
              />
            ) : null}
            {isHood &&
            !isVertical &&
            !supplementalDescription &&
            shortDescription &&
            !isShortTerm ? (
              <ListingCardDescription description={shortDescription} />
            ) : null}
            {isVertical ? <ListingCardAmenities amenitiesList={dedupedAmenitiesList} /> : null}
            {isCity && !isVertical && reviewsSummary && !isShortTerm ? (
              <ListingCardReviewsSummary reviewsSummary={reviewsSummary} />
            ) : null}
            {isCity && !isVertical && !reviewsSummary && review ? (
              <ListingCardReview review={review} />
            ) : null}
          </>
        )}
      </div>
    </Fragment>
  )
}
